import { cva } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../styles/lib/utils';

export const TOGGLE_PILL_TONES = ['on', 'off', 'accent'] as const;

export type TogglePillTone = (typeof TOGGLE_PILL_TONES)[number];

const togglePillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm font-medium transition-opacity',
  {
    variants: {
      tone: {
        on: 'border-blue-40 bg-blue-10 text-blue-80',
        off: 'border-grey-30 bg-white text-grey-90',
        accent: 'border-yellow-20 bg-yellow-10 text-grey-90',
      } satisfies Record<TogglePillTone, string>,
    },
    defaultVariants: { tone: 'off' },
  },
);

const dotClass: Record<TogglePillTone, string> = {
  on: 'bg-blue-60',
  off: 'bg-grey-40',
  accent: 'bg-yellow-60',
};

export type TogglePillProps = Omit<React.ComponentProps<'button'>, 'children'> & {
  /** Whether the toggle is on (drives the color + the default On/Off label). */
  on: boolean;
  /** Attention/override tone (yellow), takes precedence over the on/off color. */
  accent?: boolean;
  /** Optional label; defaults to "On" / "Off". */
  children?: React.ReactNode;
};

/**
 * A small pill-shaped on/off toggle button: a colored dot + label. `on` drives blue (on) vs grey
 * (off); `accent` renders the yellow "attention/override" tone. Clickable when not `disabled`
 * (shows a pointer cursor); disabled renders muted and non-interactive.
 */
function TogglePill({ on, accent = false, className, disabled, children, ...props }: TogglePillProps) {
  const tone: TogglePillTone = accent ? 'accent' : on ? 'on' : 'off';
  return (
    <button
      type="button"
      data-slot="toggle-pill"
      disabled={disabled}
      className={cn(
        togglePillVariants({ tone }),
        'focus-ring',
        disabled ? 'cursor-default opacity-60' : 'cursor-pointer hover:opacity-80',
        className,
      )}
      {...props}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dotClass[tone])} />
      {children ?? (on ? 'On' : 'Off')}
    </button>
  );
}

export { TogglePill, togglePillVariants };
